import { useState } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip, { type ChipProps } from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useVerificationDetail } from "../../../hooks/useVerificationDetail";
import { isHttpError, isValidationError } from "../../../errors/isApiError";
import { siConsoleTableSx } from "../../../theme/dataGridSx";
import {
  formatApplicantName,
  formatCheckCodeLabel,
  formatStatusLabel,
} from "../../../utils/verificationLabels";
import { getStatusOutcome } from "../../../utils/verificationStatus";
import { DeclineReasonsList } from "../../../components/DeclineReasonsList";
import { DocumentImagesDialog } from "../../../components/DocumentImagesDialog";

const BACK_DESTINATIONS: Record<string, string> = {
  "/": "Dashboard",
  "/kyc": "Pending",
};

// Map a status string to a chip color: valid/passed → green, invalid/failed → red.
function statusChipColor(status: string): ChipProps["color"] {
  const outcome = getStatusOutcome(status);
  if (outcome === "pass") return "success";
  if (outcome === "fail") return "error";
  return "default";
}

// Maps a load error to a tailored alert. The portal API returns:
//   404 verification_not_found       → the ID doesn't exist
//   409 (not yet provisioned)        → the verification exists but isn't ready
// Anything else falls back to the error's message / a generic string.
function DetailErrorAlert({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (isHttpError(error)) {
    if (error.statusCode === 404) {
      return <Alert severity="warning">{t("Verification not found.")}</Alert>;
    }
    if (error.statusCode === 409) {
      // Surface the server's exact wording (e.g. "not yet provisioned"). This verification hasn&rsquo;t been provisioned yet. Please try again shortly.
      return <Alert severity="info">{error.message}</Alert>;
    }
    return <Alert severity="error">{error.message}</Alert>;
  }
  if (isValidationError(error)) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  return <Alert severity="error">{t("Failed to load verification details.")}</Alert>;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    // Stacks label-over-value on mobile, side-by-side from the sm breakpoint up.
    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.25, sm: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: { xs: "auto", sm: 160 }, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function VerificationDetailPage() {
  const { verificationId } = useParams<{ verificationId: string }>();
  const { verification, isLoading, error } = useVerificationDetail(verificationId);
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);

  const backTo =
    typeof location.state === "object" && location.state !== null && "from" in location.state
      ? (location.state as { from: string }).from
      : "/";
  // backTo may carry a query string (e.g. "/?jurisdiction=Nevada") so filters
  // survive the round trip — strip it before the label lookup, which only
  // ever keys on the bare pathname.
  const backLabel = BACK_DESTINATIONS[backTo.split("?")[0]] ?? "Dashboard";

  const applicantName = verification
    ? formatApplicantName(verification.applicantFirstName, verification.applicantLastName)
    : "";

  // documentId isn't on the document itself — resolved via the verification's
  // separate documentReferences array, matched by type.
  function getDocumentId(documentType: string): string | undefined {
    return verification?.documentReferences.find((ref) => ref.type === documentType)?.id;
  }

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Stack spacing={0.5}>
        <Link
          component={RouterLink}
          to={backTo}
          underline="hover"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            width: "fit-content",
            color: "text.primary",
          }}
        >
          <ArrowBackIcon fontSize="inherit" />
          {t("Back to {{destination}}", { destination: t(backLabel) })}
        </Link>
        <Typography variant="subtitle1" fontWeight={700}>
          {t("Verification Detail")}
        </Typography>
      </Stack>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && Boolean(error) && <DetailErrorAlert error={error} />}

      {!isLoading && !error && !verification && (
        <Alert severity="warning">{t("No verification found for this ID.")}</Alert>
      )}

      {!isLoading && verification && (
        <>
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 1.5,
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              // Single column on mobile/tablet; two columns from md up.
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              columnGap: { md: 6 },
              rowGap: 1.5,
            }}
          >
            <Stack spacing={1.5}>
              {/* "External ID" is mapped to the API's verificationId. */}
              <DetailRow label={t("External ID")} value={verification.verificationId} />
              <DetailRow label={t("Applicant")} value={applicantName || "—"} />
              <DetailRow
                label={t("Verification Status")}
                value={
                  <Chip
                    size="small"
                    label={t(formatStatusLabel(verification.verificationStatus))}
                  />
                }
              />
              <DetailRow
                label={t("Applicant Status")}
                value={
                  <Chip
                    size="small"
                    label={t(formatStatusLabel(verification.applicantStatus))}
                    color={statusChipColor(verification.applicantStatus)}
                  />
                }
              />
            </Stack>
            <Stack spacing={1.5}>
              <DetailRow
                label={t("Decline Reasons")}
                value={<DeclineReasonsList reasons={verification.declinedReasons} />}
              />
              <DetailRow
                label={t("Created At")}
                value={new Date(verification.createdAt).toLocaleString()}
              />
              <DetailRow
                label={t("Updated At")}
                value={new Date(verification.updatedAt).toLocaleString()}
              />
            </Stack>
          </Box>

          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("Checks")}
            </Typography>
            {verification.checks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("No checks recorded.")}
              </Typography>
            ) : (
              <TableContainer
                sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflowX: "auto" }}
              >
                <Table size="small" sx={{ minWidth: 480, ...siConsoleTableSx(theme.palette.mode) }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Check")}</TableCell>
                      <TableCell>{t("Status")}</TableCell>
                      <TableCell>{t("Required")}</TableCell>
                      <TableCell>{t("Decline Reasons")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {verification.checks.map((check) => (
                      <TableRow key={check.checkCode}>
                        <TableCell>{t(formatCheckCodeLabel(check.checkCode))}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={t(formatStatusLabel(check.status))}
                            color={statusChipColor(check.status)}
                          />
                        </TableCell>
                        <TableCell>{check.isRequired ? t("Yes") : t("No")}</TableCell>
                        <TableCell>
                          <DeclineReasonsList reasons={check.declineReasons} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("Documents")}
            </Typography>
            {verification.documents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("No documents submitted.")}
              </Typography>
            ) : (
              <TableContainer
                sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflowX: "auto" }}
              >
                {/* minWidth keeps columns readable on narrow screens; the
                    container scrolls horizontally instead of crushing them. */}
                <Table size="small" sx={{ minWidth: 480, ...siConsoleTableSx(theme.palette.mode) }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Thumbnail")}</TableCell>
                      <TableCell>{t("Document Type")}</TableCell>
                      <TableCell>{t("Issue Date")}</TableCell>
                      <TableCell>{t("Expiry Date")}</TableCell>
                      <TableCell>{t("Status")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {verification.documents.map((document, index) => {
                      const documentId = getDocumentId(document.documentType);
                      return (
                        <TableRow key={`${document.documentType}-${index}`}>
                          <TableCell>
                            <IconButton
                              size="small"
                              aria-label={t("View document images")}
                              disabled={!documentId}
                              onClick={() => documentId && setOpenDocumentId(documentId)}
                            >
                              <DescriptionOutlinedIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell>{document.documentType}</TableCell>
                          <TableCell>
                            {document.issueDate
                              ? new Date(document.issueDate).toLocaleDateString()
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {document.expiryDate
                              ? new Date(document.expiryDate).toLocaleDateString()
                              : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t(formatStatusLabel(document.status))}
                              color={statusChipColor(document.status)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("Database Screening")}
            </Typography>
            {!verification.databaseScreening ||
            verification.databaseScreening.matches.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("No database screening matches.")}
              </Typography>
            ) : (
              <TableContainer
                sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflowX: "auto" }}
              >
                <Table size="small" sx={{ minWidth: 480, ...siConsoleTableSx(theme.palette.mode) }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Name")}</TableCell>
                      <TableCell>{t("Type")}</TableCell>
                      <TableCell>{t("Score")}</TableCell>
                      <TableCell>{t("Source")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {verification.databaseScreening.matches.map((match, index) => (
                      <TableRow key={`${match.url}-${index}`}>
                        <TableCell>{match.name}</TableCell>
                        <TableCell>{match.type}</TableCell>
                        <TableCell>{match.score}</TableCell>
                        <TableCell>
                          <Link href={match.url} target="_blank" rel="noopener noreferrer">
                            {t("View source")}
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </>
      )}

      {verificationId && openDocumentId && (
        <DocumentImagesDialog
          open={Boolean(openDocumentId)}
          onClose={() => setOpenDocumentId(null)}
          verificationId={verificationId}
          documentId={openDocumentId}
        />
      )}
    </Stack>
  );
}

export default VerificationDetailPage;
